import { CAPABILITIES, isCapability, type Capability } from './capabilities';
import { Profile, profileFromRole } from './profiles';
import {
  ALL_SCOPES,
  channelFromRole,
  EMPTY_SCOPES,
  type BookingChannel,
  type Scopes,
  type ScopeKey,
} from './scopes';

const PANEL_CLIENT: string = import.meta.env.VITE_SSO_CLIENT;
const STAFF_REALM_ROLES: readonly string[] = ['admin', 'staff'];

export interface JwtPayload {
  exp?: number;
  iss?: string;
  azp?: string;
  email?: string;
  preferred_username?: string;
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] } | undefined>;
}

export class AuthSession {
  private constructor(
    private readonly capabilities: ReadonlySet<Capability>,
    readonly profiles: readonly Profile[],
    private readonly scopes: Scopes,
    readonly isStaff: boolean,
  ) {}

  static empty(): AuthSession {
    return new AuthSession(new Set<Capability>(), [], EMPTY_SCOPES, false);
  }

  /** Sin ningún rol del panel el usuario es Admin. */
  static fromToken(payload: JwtPayload | null): AuthSession {
    if (!payload) return AuthSession.empty();

    const roles = payload.resource_access?.[PANEL_CLIENT]?.roles ?? [];
    const profiles = unique(
      roles
        .map(profileFromRole)
        .filter((profile): profile is Profile => profile !== null),
    );
    const channels = unique(
      roles
        .map(channelFromRole)
        .filter((channel): channel is BookingChannel => channel !== null),
    );
    const capabilities = new Set<Capability>(roles.filter(isCapability));
    const isLegacy =
      profiles.length === 0 && channels.length === 0 && capabilities.size === 0;

    return new AuthSession(
      isLegacy ? new Set<Capability>(CAPABILITIES) : capabilities,
      isLegacy ? [Profile.ADMIN] : profiles,
      isLegacy ? ALL_SCOPES : { 'booking.channels': channels },
      readIsStaff(payload),
    );
  }

  canAccess(requirement: Capability | null | undefined): boolean {
    return !requirement || this.capabilities.has(requirement);
  }

  scope<K extends ScopeKey>(key: K): Scopes[K] {
    return this.scopes[key];
  }
}

export function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const raw = token.startsWith('Bearer ') ? token.slice(7) : token;
    const base64 = raw.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json) as JwtPayload;
  } catch {
    return null;
  }
}

function readIsStaff(payload: JwtPayload): boolean {
  const realmRoles = payload.realm_access?.roles ?? [];
  return realmRoles.some(role => STAFF_REALM_ROLES.includes(role));
}

function unique<T>(values: readonly T[]): T[] {
  return [...new Set(values)];
}
